'use client'

import * as React from 'react'
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import { cn } from '@/utils/styles/cn'
import { CheckIcon, ChevronRightIcon, DotFilledIcon } from '@radix-ui/react-icons'
import { motion } from 'framer-motion'

const DropdownMenu = DropdownMenuPrimitive.Root

const DropdownMenuTrigger = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Trigger>
>(({ className, ...props }, ref) => (
    <DropdownMenuPrimitive.Trigger
        ref={ref}
        className={cn('focus:outline-none focus-visible:outline-none focus-within:outline-none', className)}
        {...props}
    />
))
DropdownMenuTrigger.displayName = DropdownMenuPrimitive.Trigger.displayName

const DropdownMenuGroup = DropdownMenuPrimitive.Group

const DropdownMenuPortal = DropdownMenuPrimitive.Portal

const DropdownMenuSubContentContext = React.createContext<React.MutableRefObject<HTMLDivElement | null> | null>(null)

/**
 * Wrapping Radix's Sub so a scroll outside the open submenu closes it — Radix keeps the portaled
 * submenu anchored to its trigger, so scrolling the parent list otherwise drags it past the edge.
 */
const DropdownMenuSub = ({
    open: openProp,
    onOpenChange,
    defaultOpen,
    children,
    ...rest
}: React.ComponentProps<typeof DropdownMenuPrimitive.Sub>) => {
    const [internalOpen, setInternalOpen] = React.useState<boolean>(defaultOpen ?? false)
    const isControlled = openProp !== undefined
    const currentOpen = isControlled ? Boolean(openProp) : internalOpen
    const setOpen = React.useCallback(
        (next: boolean) => {
            if (!isControlled) setInternalOpen(next)
            onOpenChange?.(next)
        },
        [isControlled, onOpenChange]
    )
    const contentRef = React.useRef<HTMLDivElement | null>(null)

    React.useEffect(() => {
        if (!currentOpen) return
        const handleScroll = (event: Event) => {
            const target = event.target as Node | null
            const el = contentRef.current
            if (el && target && el.contains(target)) return
            setOpen(false)
        }
        window.addEventListener('scroll', handleScroll, { capture: true, passive: true })
        return () =>
            window.removeEventListener('scroll', handleScroll, { capture: true } as EventListenerOptions)
    }, [currentOpen, setOpen])

    return (
        <DropdownMenuSubContentContext.Provider value={contentRef}>
            <DropdownMenuPrimitive.Sub open={currentOpen} onOpenChange={setOpen} {...rest}>
                {children}
            </DropdownMenuPrimitive.Sub>
        </DropdownMenuSubContentContext.Provider>
    )
}

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup

const DropdownMenuSubTrigger = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
        inset?: boolean
        withChevron?: boolean
    }
>(({ className, inset, children, withChevron = true, ...props }, ref) => (
    <DropdownMenuPrimitive.SubTrigger
        ref={ref}
        className={cn(
            'flex cursor-default gap-2 select-none items-center rounded-sm p-2 text-sm outline-hidden focus:bg-accent data-[state=open]:bg-accent [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 focus-within:outline-hidden focus:outline-hidden focus-border-none',
            'data-disabled:opacity-50 data-disabled:cursor-not-allowed data-disabled:pointer-events-none',
            inset && 'pl-8',
            className
        )}
        {...props}
    >
        {children}
        {withChevron && !props.disabled && <ChevronRightIcon className="ml-auto" />}
    </DropdownMenuPrimitive.SubTrigger>
))
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName

const DropdownMenuSubContent = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent> & {
        /** Portals the submenu to document body — avoids overflow clipping in nested menus. */
        portaled?: boolean
    }
>(({ className, portaled, ...props }, forwardedRef) => {
    // Registers this SubContent's DOM node with the wrapping Sub so it can distinguish scrolls
    // inside the open submenu (keep open) from scrolls outside it (close).
    const registerContentRef = React.useContext(DropdownMenuSubContentContext)
    const setRef = React.useCallback(
        (node: HTMLDivElement | null) => {
            if (registerContentRef) registerContentRef.current = node
            if (typeof forwardedRef === 'function') forwardedRef(node)
            else if (forwardedRef)
                (forwardedRef as React.MutableRefObject<HTMLDivElement | null>).current = node
        },
        [forwardedRef, registerContentRef]
    )

    const content = (
        <DropdownMenuPrimitive.SubContent
            ref={setRef}
            className={cn(
                'z-100 min-w-32 overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
                className
            )}
            {...props}
        />
    )
    return portaled ? <DropdownMenuPrimitive.Portal>{content}</DropdownMenuPrimitive.Portal> : content
})
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName

const DropdownMenuContent = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> & {
        contentClassName?: string
        portalContainer?: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Portal>['container']
    }
>(({ className, contentClassName, children, sideOffset = 4, portalContainer, ...props }, ref) => {
    const componentId = React.useId()

    return (
        <DropdownMenuPrimitive.Portal container={portalContainer}>
            <DropdownMenuPrimitive.Content
                ref={ref}
                sideOffset={sideOffset}
                className={cn('z-100', contentClassName)}
                {...props}
            >
                <motion.div
                    style={{ willChange: 'opacity, transform' }}
                    initial={{ opacity: 0, y: 10, scale: 0.7 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    key={'dropdown-menu-content-' + componentId}
                    exit={{
                        opacity: 0,
                        y: 0,
                        scale: 0.7,
                        transition: {
                            opacity: { duration: 0.12 },
                            y: { type: 'spring', bounce: 0.01, duration: 0.15 },
                            scale: { type: 'spring', bounce: 0.01, duration: 0.15 },
                        },
                    }}
                    transition={{
                        opacity: { duration: 0.1 },
                        y: { type: 'spring', bounce: 0.01, duration: 0.12 },
                        scale: { type: 'spring', bounce: 0.01, duration: 0.12 },
                    }}
                    className={cn(
                        'min-w-32 rounded-md border bg-popover p-1 text-popover-foreground shadow-md',
                        'data-[state=open]:shadow-2xl',
                        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
                        className
                    )}
                >
                    {children}
                </motion.div>
            </DropdownMenuPrimitive.Content>
        </DropdownMenuPrimitive.Portal>
    )
})

DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName

const DropdownMenuItem = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
        inset?: boolean
    }
>(({ className, onClick, inset, ...props }, ref) => (
    <DropdownMenuPrimitive.Item
        ref={ref}
        className={cn(
            'relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden focus:bg-accent focus:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50 [&>svg]:size-4 [&>svg]:shrink-0',
            'gap-2 p-2',
            'cursor-pointer data-highlighted:bg-stone-100 data-highlighted:text-stone-900',
            inset && 'pl-8',
            className
        )}
        onClick={(e) => {
            e.stopPropagation()
            onClick?.(e)
        }}
        {...props}
    />
))
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName

const DropdownMenuCheckboxItem = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({ className, children, checked, ...props }, ref) => (
    <DropdownMenuPrimitive.CheckboxItem
        ref={ref}
        className={cn(
            'group relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-hidden transition-colors focus:bg-accent focus:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50',
            className
        )}
        checked={checked}
        {...props}
    >
        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center rounded-sm border border-current opacity-0 group-hover:opacity-50 group-data-[state=checked]:opacity-50 transition-opacity">
            <DropdownMenuPrimitive.ItemIndicator className="flex items-center justify-center text-current">
                <CheckIcon className="h-3.5 w-3.5" />
            </DropdownMenuPrimitive.ItemIndicator>
        </span>
        {children}
    </DropdownMenuPrimitive.CheckboxItem>
))
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName

const DropdownMenuRadioItem = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({ className, children, ...props }, ref) => (
    <DropdownMenuPrimitive.RadioItem
        ref={ref}
        className={cn(
            'relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-hidden transition-colors focus:bg-accent focus:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50',
            className
        )}
        {...props}
    >
        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
            <DropdownMenuPrimitive.ItemIndicator>
                <DotFilledIcon className="h-2 w-2 fill-current" />
            </DropdownMenuPrimitive.ItemIndicator>
        </span>
        {children}
    </DropdownMenuPrimitive.RadioItem>
))
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName

const DropdownMenuLabel = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.Label>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
        inset?: boolean
    }
>(({ className, inset, ...props }, ref) => (
    <DropdownMenuPrimitive.Label
        ref={ref}
        className={cn('px-2 py-1.5 text-sm font-semibold', inset && 'pl-8', className)}
        {...props}
    />
))
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName

const DropdownMenuSeparator = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
    <DropdownMenuPrimitive.Separator ref={ref} className={cn('-mx-1 my-1 h-px bg-muted', className)} {...props} />
))
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName

const DropdownMenuShortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => {
    return <span className={cn('ml-auto text-xs tracking-widest opacity-60', className)} {...props} />
}
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut'

export {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuCheckboxItem,
    DropdownMenuRadioItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuShortcut,
    DropdownMenuGroup,
    DropdownMenuPortal,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuRadioGroup,
}
